<template>
  <v-card class="mb-8" elevation="0">
    <v-card-text>
      <div class="subtitle-1 font-weight-bold mb-4">
        <v-icon left>mdi-eye-check</v-icon>
        {{ $t("overview") }}
      </div>

      <!-- Vizuelni pregled konfiguracije -->
      <div class="mb-4">
        <v-alert type="info" border="left" colored-border elevation="2">
          <div class="subtitle-2 font-weight-bold mb-3">
            {{ $t("how_your_limit_will_work") }}
          </div>

          <v-row dense>
            <!-- Tip limita -->
            <v-col cols="12" sm="6">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-chart-box</v-icon>
                <strong>{{ $t("limit_type") }}:</strong>
                <span class="ml-2">{{
                  this.getLimitTypeLabel(config.limit_definition?.active)
                }}</span>
              </div>
            </v-col>

            <!-- Vremenski limit -->
            <v-col cols="12" sm="6">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-clock-outline</v-icon>
                <strong>{{ $t("time_limit") }}:</strong>
                <span class="ml-2">{{ getTimeLimitLabel(config.timing?.active) }}</span>
              </div>
            </v-col>

            <!-- Ko plaća -->
            <v-col cols="12" sm="6">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-account-cash</v-icon>
                <strong>{{ $t("who_pays") }}:</strong>
                <span class="ml-2">
                  {{ getWhoPaysLabel(config.payment_logic?.active) }}
                </span>
              </div>
            </v-col>

            <!-- Šta može da naruči -->
            <v-col cols="12" sm="6">
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-food-variant</v-icon>
                <strong>{{ $t("what_can_order") }}:</strong>
                <span class="ml-2">
                  {{ getWhatCanOrderLabel(config.ordering_rules?.active) }}
                </span>
              </div>
            </v-col>

            <!-- Breakdown info -->
            <v-col cols="12" v-if="config.breakdown?.enabled">
              <v-divider class="my-2" />
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-arrange-bring-forward</v-icon>
                <strong>{{ $t("limit_breakdown") }}:</strong>
              </div>
              <v-chip-group column>
                <v-chip small v-if="config.breakdown.byShift">
                  <v-icon small left>mdi-clock-time-four-outline</v-icon>
                  {{ $t("by_shift") }}
                </v-chip>
                <v-chip small v-if="config.breakdown.byCategory">
                  <v-icon small left>mdi-shape-outline</v-icon>
                  {{ $t("by_category") }}
                </v-chip>
                <v-chip small v-if="config.breakdown.byGroup">
                  <v-icon small left>mdi-folder-outline</v-icon>
                  {{ $t("by_group") }}
                </v-chip>
                <v-chip small v-if="config.breakdown.byDayOfWeek">
                  <v-icon small left>mdi-calendar-week</v-icon>
                  {{ $t("by_day_of_week") }}
                </v-chip>
              </v-chip-group>
            </v-col>

            <!-- Rollover info -->
            <v-col cols="12" v-if="config.rollover_policy?.allow_transfer">
              <v-divider class="my-2" />
              <div class="d-flex align-center">
                <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
                <span
                  >{{ $t("rollover_enabled") }}:
                  {{ getRolloverLabel(config.rollover_policy.transfer_period) }}</span
                >
              </div>
            </v-col>

            <!-- Odabrani restorani -->
            <v-col cols="12" v-if="selectedRestaurants.length > 0">
              <v-divider class="my-2" />
              <div class="d-flex align-center mb-2">
                <v-icon small class="mr-2">mdi-store</v-icon>
                <strong>{{ $t("selected_restaurants") }}:</strong>
                <v-chip x-small class="ml-2" color="deep-purple" dark>
                  {{ selectedRestaurants.length }}
                </v-chip>
              </div>
              <div class="d-flex flex-wrap mb-2">
                <v-chip
                  v-for="r in selectedRestaurants"
                  :key="r.id"
                  small
                  class="mr-1 mb-1"
                  outlined
                  color="deep-purple"
                >
                  <v-icon x-small left>mdi-store</v-icon>
                  {{ r.name }}
                </v-chip>
              </div>
              <div class="d-flex align-center">
                <v-icon small class="mr-2">{{ limit?.scope_type === 'individual' ? 'mdi-store-outline' : 'mdi-pool' }}</v-icon>
                <strong>{{ $t("restaurant_limit_mode_label") }}:</strong>
                <span class="ml-2">{{ limit?.scope_type === 'individual' ? $t('restaurant_limit_mode_individual') : $t('restaurant_limit_mode_pool') }}</span>
              </div>
            </v-col>
          </v-row>
        </v-alert>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  getLimitTypeLabelKey,
  getTimeLimitLabelKey,
  getWhoPaysLabelKey,
  getWhatCanOrderLabelKey,
  getRolloverLabelKey,
} from "@/utils/limitHelpers";

export default {
  name: "Step5Overview",
  props: {
    config: {
      type: Object,
      required: true,
    },
    limit: {
      type: Object,
      default: null,
    },
  },
  computed: {
    selectedRestaurants() {
      return Array.isArray(this.limit?.restaurants) ? this.limit.restaurants : [];
    },
  },
  methods: {
    getLimitTypeLabel(type) {
      const key = getLimitTypeLabelKey(type);
      return key ? this.$t(key) : "-";
    },
    getTimeLimitLabel(type) {
      const key = getTimeLimitLabelKey(type);
      return key ? this.$t(key) : "-";
    },
    getWhoPaysLabel(type) {
      const key = getWhoPaysLabelKey(type);
      return key ? this.$t(key) : "-";
    },
    getWhatCanOrderLabel(type) {
      if (Array.isArray(type)) {
        if (type.length === 0) return "-";
        return type
          .map((t) => {
            const key = getWhatCanOrderLabelKey(t);
            return key ? this.$t(key) : "";
          })
          .filter(Boolean)
          .join(", ");
      }
      const key = getWhatCanOrderLabelKey(type);
      return key ? this.$t(key) : "-";
    },
    getRolloverLabel(type) {
      const key = getRolloverLabelKey(type);
      return key ? this.$t(key) : "-";
    },
  },
};
</script>
